import { Files, PencilSimpleLine } from "@phosphor-icons/react";
import { useState } from "react";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { Tag } from "common/components/ui/Tag";
import { useCampaignContext } from "common/helpers/CampaignContext";
import type { CampaignType, ProfileSearchParams } from "common/types";

import useTargeting from "../../datahooks/useTargeting";
import ResetTargeting from "../ResetTargeting";
import DripTargetingLabels from "./DripTargetingLabels";
import EventTargetingLabel from "./EventTargetingLabel";
import GroupTargetingLabel from "./GroupTargetingLabel";
import NavigatorTargetingLabels from "./NavigatorTargetingLabels";
import PostTargetingLabel from "./PostTargetingLabel";
import SearchTargetingLabels from "./SearchTargetingLabels";

import type {
  DripTargeting,
  EventTargeting,
  GroupTargeting,
  NavigatorFiltersTargeting,
  NavigatorUrlTargeting,
  PostTargeting,
} from "../../types";

interface TargetingPreviewProps {
  shouldShowEditButton?: boolean;
  isEditDisabled?: boolean;
}

export default function TargetingPreview({
  shouldShowEditButton = false,
  isEditDisabled = false,
}: TargetingPreviewProps) {
  const { type } = useCampaignContext();
  const { targeting, isLoadingTargeting } = useTargeting();
  const [isResetTargetingOpen, setIsResetTargetingOpen] = useState(false);

  const targetingTagOptions: Record<CampaignType, React.ReactNode> = {
    search: (
      <SearchTargetingLabels
        searchParams={targeting as ProfileSearchParams}
        isLoading={isLoadingTargeting}
      />
    ),
    event: (
      <EventTargetingLabel
        eventId={(targeting as EventTargeting)?.event_id}
        isLoading={isLoadingTargeting}
      />
    ),
    group: (
      <GroupTargetingLabel
        groupId={(targeting as GroupTargeting)?.group_id}
        isLoading={isLoadingTargeting}
      />
    ),
    post: (
      <PostTargetingLabel
        postId={(targeting as PostTargeting)?.post_id}
        isLoading={isLoadingTargeting}
      />
    ),
    list: (
      <Tag size="lg" variant="primary-black" leftIcon={<Files weight="fill" />}>
        List
      </Tag>
    ),
    drip: (
      <DripTargetingLabels
        targeting={targeting as DripTargeting}
        isLoading={isLoadingTargeting}
      />
    ),
    navigator: (
      <NavigatorTargetingLabels
        targeting={
          targeting as NavigatorFiltersTargeting | NavigatorUrlTargeting
        }
        isLoading={isLoadingTargeting}
      />
    ),
    crm: (
      <Tag size="lg" variant="primary-black" leftIcon={<Files weight="fill" />}>
        List
      </Tag>
    ),
  };

  return (
    <section className="flex flex-col items-start rounded-3xl border border-black-200 p-3">
      <div className="mb-2 flex w-full justify-between border-b border-black-200 pb-2">
        <div className="flex flex-col gap-y-0.5">
          <h3 className="text-button-md">Targeting</h3>
          <p className="text-xs text-black-500">
            Your targeting configuration for this campaign
          </p>
        </div>

        <RenderIf condition={shouldShowEditButton}>
          <Button
            variant="secondary-black"
            intent="iconOnly"
            disabled={isEditDisabled}
            onClick={() => setIsResetTargetingOpen(true)}
          >
            <PencilSimpleLine />
          </Button>
          <ResetTargeting
            isOpen={isResetTargetingOpen}
            setIsOpen={setIsResetTargetingOpen}
          />
        </RenderIf>
      </div>

      {targetingTagOptions[type]}
    </section>
  );
}
